import { Component, ElementRef, Input, OnInit, ViewChild, } from '@angular/core';
import {
  AssistantService,
  AssistantThreadModel,
  ChatHistoryListModel,
  ChatHistoryModel,
  ChatSettings,
  IndexAdminModel,
  IndexService,
  MessageRole, ReasoningEffort,
} from 'digiteq-ai-portal-client-lib';
import { Confirmation, ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { AppSettingsService } from '../../services/app-settings.service';
import { Sidebar } from 'primeng/sidebar';
import { FeMessageModel } from '../../models/fe-message.model';
import { ChatType } from '../../models/chat-type';
import { Subject } from 'rxjs';
import { ErrorService } from '../../services/error.service';
import _ from 'lodash';
import { appConstants } from '../../constants/app-constants';
import { ThreadMessageModel } from 'digiteq-ai-portal-client-lib/model/threadMessageModel';
import { FileUtils } from '../../utils/file-utils.component';
import { AiAssistantService } from '../../services/ai-assistant.service';
import { CompanyChatService } from '../../company-chat.service';
import { FileModel, FileUploadEnum } from '../../models/file-model';
import { ChatViewService } from '../../chat-view.service';
import { ContactUsService } from '../../services/contact-us.service';

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
})
export class ChatComponent implements OnInit {
  @ViewChild("messagesContainer") messagesContainer: ElementRef<HTMLElement>;
  @ViewChild("settingsSidebar") settingsSidebar: Sidebar;

  @Input() description: string;

  items: MenuItem[] | undefined;
  history: ChatHistoryListModel[];
  availableIndexes: IndexAdminModel[] = [];
  reloadChatHistory$: Subject<void> = new Subject();
  role = MessageRole;

  readonly appConstants = appConstants;
  readonly chatTypeEnum = ChatType;
  readonly confirmationOptions: Confirmation = {
    target: null,
    header: 'Confirmation',
    icon: 'pi pi-exclamation-triangle',
    rejectButtonStyleClass: 'p-button-text',
  }

  get chatType(): ChatType {
    return this.chatViewService.chatType;
  }

  @Input() set chatType(value: ChatType) {
    this.chatViewService.chatType = value;
  }

  get historyVisible(): boolean {
    return this.settingsService.historyVisible;
  }

  set historyVisible(value: boolean) {
    if (this.settingsService.historyVisible !== value) {
      this.settingsService.historyVisible = value;
    }
  }

  get settingsVisible(): boolean {
    return this.settingsService.settingsVisible;
  }

  set settingsVisible(value: boolean) {
    if (this.settingsService.settingsVisible !== value) {
      this.settingsService.settingsVisible = value;
      this.handleVisibilityOfContactUsIcons(value);
    }
  }

  set currentChatId(value: string | number | undefined) {
    this.chatViewService.currentChatId = value;
    if (this.isAssistant()) {
      this.aiAssistantService.threadId = value as number | undefined;
    }
  }

  get messages(): FeMessageModel[] {
    return this.chatViewService.messages;
  }

  get showWelcomeMessage(): boolean {
    return this.chatViewService.showWelcomeMessage;
  }

  constructor(
    private readonly chatViewService: ChatViewService,
    private readonly settingsService: AppSettingsService,
    private readonly errorService: ErrorService,
    private readonly confirmationService: ConfirmationService,
    private readonly assistantService: AssistantService,
    private readonly aiAssistantService: AiAssistantService,
    private readonly daiseService: CompanyChatService,
    private readonly indexService: IndexService,
    private readonly messageService: MessageService,
    private readonly contactUsService: ContactUsService,
  ) { }

  ngOnInit() {
    if (this.chatType === ChatType.COMPANY) {
      this.loadDaiseIndexes();
    }
    if (this.chatViewService) {
      this.chatViewService.reset();
      this.chatViewService.messageSent$.subscribe(() => {
        this.scrollToBottom();
      })
      this.chatViewService.onChatResponseComplete$.subscribe(() => {
        this.handleChatComplete();
      })
    }
  }

  handleChatComplete() {
    const lastMessage = this.messages[this.messages.length - 1];
    lastMessage.completed = true;
    lastMessage.message = this.searchForPatternsToReplace(lastMessage.message, this.chatViewService.assistantAttachments);
    // TODO: there might be a better way than call API
    this.reloadChatHistory$.next();
  }

  searchForPatternsToReplace(text: string, attachments: string[], images?: string[]): string {
    const linkPattern = /(?<!!)\[.*?]\(.*?\)/g;
    if (text.includes("[") && text.includes("](")) {
      const matches = text.match(linkPattern);
      matches?.forEach((match, index) => {
        const contentMatch = (/\[(.*?)]/).exec(match);
        if (attachments[index]) {
          const downloadPlaceholder = `${ contentMatch[0] }(download%%${ attachments[index] }%%)`;
          text = text.replace(match, downloadPlaceholder);
        }
      })
    }

    const imagePattern = /!\[.*?]\(.*?\)/g;
    if (text.includes("![") && text.includes("](")) {
      const matches = imagePattern.exec(text);
      matches.forEach((match, index) => {
        const contentMatch = (/\[(.*?)]/).exec(match);
        const imageId = this.getImageId(index, images);
        if (imageId) {
          this.downloadAndReplaceImage(imageId, contentMatch, match, text);
        }
      });
    }
    return this.prepareLatex(text);
  }

  prepareLatex(text: string): string {
    const latexInlinePatternOne = /(?<!\\)\\\(([\s\S.]*?)(?<!\\)\\\)/g;
    const latexInlinePatterTwo = /(?<!\\)\\\[([\s\S.]*?)(?<!\\)\\]/g;
    const latexBlockPatternOne = /\\\\\(([\s\S.]*?)\\\\\)/g;
    const latexBlockPatterTwo = /\\\\\[([\s\S.]*?)\\\\]/g;

    text = text.replace(latexInlinePatternOne, (match, p1) => `$${ p1 }$`);
    text = text.replace(latexInlinePatterTwo, (match, p1) => `$${ p1 }$`);
    text = text.replace(latexBlockPatternOne, (match, p1) => `$$${ p1 }$$`);
    text = text.replace(latexBlockPatterTwo, (match, p1) => `$$${ p1 }$$`);

    return text;
  }

  private getImageId(index: number, images?: string[]): string | undefined {
    let imageId: string;
    if (images?.length && images?.length > index) {
      imageId = images[index]
    } else if (
      this.chatViewService.images?.length && this.chatViewService.images?.length > index
    ) {
      imageId = this.chatViewService.images[index]
    }
    return imageId;
  }

  private downloadAndReplaceImage(attachment: string, contentMatch: RegExpExecArray, match: string, text: string) {
    this.assistantService.downloadGeneratedFile(attachment).subscribe({
      next: response => {
        const image = new Blob([response], {type: 'image/png'});
        const url = URL.createObjectURL(image);
        replaceImageSrc(attachment, url);
        const imagePlaceholder = `!${ contentMatch[0] }(${ url })`;
        text = text.replace(match, imagePlaceholder);
        this.scrollToBottom(10)
      },
      error: err => {
        this.errorService.handleError(err, 'Could not get image.');
      }
    });

    const replaceImageSrc = (attachment: string, url: string) => {
      const imgElements = document.getElementsByTagName('img');
      Array.from(imgElements).forEach(imgElement => {
        if (imgElement.src.includes(attachment)) {
          imgElement.src = url;
          imgElement.style.width = '100%';
        }
      });
    }
  }

  scrollToBottom(delay?: number): void {
    setTimeout(() => {
      this.messagesContainer.nativeElement.scrollTo({top: this.messagesContainer.nativeElement.scrollHeight});
    }, delay);
  }

  reset() {
    this.chatViewService.reset();
  }

  loadChatHistoryItem(chat: ChatHistoryModel | AssistantThreadModel) {
    this.chatViewService.messageForm.reset();
    this.reset();

    chat.messages.forEach(m => {
      if (m.role == MessageRole.User) {
        this.chatViewService.addMessage(m);
      } else {
        let feMsg: FeMessageModel
        if (this.isAssistant()) {
          const tm = m as ThreadMessageModel;

          if (tm.codeInterpreterOutput?.length) {
            feMsg = {
              role: tm.role,
              message: '```\n' + tm.codeInterpreterOutput + "```\n"
            }
          } else {
            if (tm.images?.length) {
              const imagesString = tm.images.map((img: string) => `![ai-image](${ img })\n\n`).join('');
              tm.message = imagesString + tm.message;
            }
            feMsg = {
              message: this.searchForPatternsToReplace(tm.message, tm.responseFiles.map(f => f.fileId), tm.images),
              role: tm.role,
            };
          }
        } else {
          feMsg = {
            message: this.prepareLatex(m.message),
            citations: m.citations,
            role: m.role,
          };
          this.chatViewService.updateCitationIndexes(feMsg.citations);
          this.chatViewService.updateCitations(feMsg);
        }

        this.messages.push(feMsg);
      }
    });

    if (!this.isAssistant()) {
      this.handleLoadedSettings((chat as ChatHistoryModel));
    } else {
      this.aiAssistantService.threadSearchFiles = (chat as AssistantThreadModel).searchFiles?.map(f => new FileModel([], f.name, {}, FileUploadEnum.UPLOADED, f.id));
      this.aiAssistantService.threadCodeInterpreterFiles = (chat as AssistantThreadModel).interpreters?.map(f => new FileModel([], f.name, {}, FileUploadEnum.UPLOADED, f.id));
    }
    this.currentChatId = chat[this.getChatIdProperty()]

    this.chatViewService.aiResponseError = false;
    this.chatViewService.messageForm.enable();
    this.chatViewService.stopped = false;
    this.scrollToBottom();
  }

  handleLoadedSettings(chat: ChatHistoryModel) {
    if (this.chatType === this.chatTypeEnum.GENERIC) {
      this.handleGenericChatSettings(chat.settings);
      return;
    }

    if (this.chatType === this.chatTypeEnum.COMPANY) {
      this.handleDaiseIndex(chat.daiseIndex);
    }
  }

  handleGenericChatSettings(settings: ChatSettings) {
    if (!settings) {
      return;
    }
    if(!settings?.reasoningEffortLevel) {
      settings.reasoningEffortLevel = ReasoningEffort.Medium;
    }
    if (_.isEqual(settings, this.settingsService.savedSettings)) {
      return;
    }

    this.confirmationService.confirm({
      ...this.confirmationOptions,
      message: 'Do you want to accept settings of the loaded chat?',
      accept: () => {
        this.settingsService.savedSettings = settings;
        this.settingsService.selectModel(settings.deploymentId);
      },
    });
  }

  handleDaiseIndex(daiseIndex: number) {
    if (!daiseIndex) {
      return;
    }

    const foundDaiseIndex = this.availableIndexes.find(i => i.id === daiseIndex);
    if (!foundDaiseIndex) {
      this.setDefaultIndex();
      this.messageService.add({
        severity: 'info',
        summary: 'Info',
        detail: 'Index of loaded chat not found. Default index will be used.',
      });
      return;
    }
    this.daiseService.daiseIndex = foundDaiseIndex;
  }


  isAssistant() {
    return this.chatViewService.isAssistant();
  }

  getChatIdProperty() {
    return this.isAssistant() ? 'threadId' : 'chatId';
  }

  getAcceptedTypesFormatted(acceptedFiles: string): string {
    return FileUtils.getAcceptedTypesFormatted(acceptedFiles);
  }

  loadDaiseIndexes() {
    this.indexService.getIndexes()
      .subscribe({
        next: indexes => {
          this.availableIndexes = indexes;
          this.setDefaultIndex();
        },
        error: err => {
          this.errorService.handleError(err, 'Could not load indexes');
        }
      })
  }

  setDefaultIndex() {
    const defaultIndex = this.availableIndexes.find(i => i.default);
    if (defaultIndex) {
      this.daiseService.daiseIndex = defaultIndex;
    }
  }

  handleVisibilityOfContactUsIcons(visible: boolean) {
    this.contactUsService.showContactUsIconInApp = !visible;
    this.contactUsService.showContactUsIconInChatInput = visible;
  }

  onSettingsVisibleEvent(settingsVisible: boolean) {
    this.settingsVisible = settingsVisible
  }
}
